<template>
    <div class="flex flex-col p-4 md:p-6 md:pt-4 w-full bg-surface text-ink rounded-2xl">
        <div class="flex justify-end items-center">
            <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('common.close')" @click="emit('dismiss')">
                <i class="bi bi-x-lg icon-semibold" aria-hidden="true"></i>
            </BaseButton>
        </div>

        <div class="flex gap-2 items-center flex-col md:flex-row md:h-72">
            <BaseCarousel v-model="activeId" :item-ids="itemIds" hide-controls class="grow" style="--translate-y-px: 12px">
                <!-- Panel 1: Welcome (full-width, centered) -->
                <template #item-welcome>
                    <div class="flex flex-col items-center justify-center md:h-72 text-center gap-4 px-4" :class="activeId !== 'welcome' && 'h-0'">
                        <div class="flex size-11.5 items-center justify-center rounded-lgr bg-chip-nadia-bg text-chip-nadia-text">
                            <i class="bi bi-stars text-xl" aria-hidden="true"></i>
                        </div>
                        <div class="leading-tight" :class="activeId === 'welcome' && 'animate-stagger-in'">
                            <h3 class="display-serif mb-2">
                                {{ firstName ? t("chat.home.coachingContext.greetingWithName", { name: firstName }) : t("chat.home.coachingContext.greeting") }}
                            </h3>
                            <p class="nadia-chat">{{ t("chat.home.coachingContext.tagline") }}</p>
                        </div>
                        <p class="nadia-chat text-ink-muted max-w-md" :class="activeId === 'welcome' && 'animate-stagger-in'" style="--stagger-delay: 0.1s">
                            {{ t("chat.home.coachingContext.intro") }}
                        </p>
                    </div>
                </template>

                <template v-for="panel in contextPanels" :key="panel.id" #[`item-${panel.id}`]>
                    <div class="flex flex-col md:h-72 gap-4 md:px-4 md:pt-10 items-start">
                        <div class="flex size-11.5 items-center justify-center rounded-lgr" :style="{ backgroundColor: panel.color }">
                            <component :is="panel.iconComponent" v-if="panel.iconComponent" class="text-white h-5" />
                            <i v-else class="bi text-xl text-white icon-semibold" :class="panel.icon" aria-hidden="true"></i>
                        </div>
                        <h3 class="display-serif" :class="activeId === panel.id && 'animate-stagger-in'">{{ panel.heading }}</h3>
                        <p class="body-small text-ink-muted max-w-md" :class="activeId === panel.id && 'animate-stagger-in'" style="--stagger-delay: 0.1s">
                            {{ panel.copy }}
                        </p>
                    </div>
                </template>
            </BaseCarousel>

            <div
                class="flex items-center justify-center pointer-events-none transition-opacity duration-300 basis-2/3 md:mt-10"
                :class="hasGraph ? 'opacity-100' : 'hidden opacity-0'">
                <CoachingContextGraph :active-step="graphStep" :connections-enabled="connectionsEnabled" class="w-full h-full" />
            </div>
        </div>

        <!-- Progress dots: always rendered to hold height; invisible on the welcome panel -->
        <div class="flex items-center px-4 mt-3 h-4 gap-2 transition-opacity duration-300" :class="activeId === 'welcome' ? 'invisible' : ''">
            <div v-for="(dim, idx) in dimensionDots" :key="idx" class="flex justify-center">
                <div
                    class="h-2 rounded-full bg-border-strong transition-[width,background-color] duration-300"
                    :class="idx === activeDimensionIndex ? 'w-5.5' : 'w-2'"
                    :style="{ backgroundColor: idx <= activeDimensionIndex ? dim.dotColor : undefined }" />
            </div>
        </div>

        <!-- Fixed footer region: back arrow + forward nav OR CTA pill -->
        <div class="flex items-center gap-3 px-4 mt-3 h-10">
            <!-- Back arrow (hidden on first slide) -->
            <BaseButton v-if="currentIndex > 0" theme="neutral" size="sm" icon-only :aria-label="t('chat.home.coachingContext.previousSlide')" @click="advanceTo(currentIndex - 1)">
                <i class="bi bi-arrow-left" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton v-if="currentIndex < itemIds.length - 1" theme="neutral" size="sm" class="inline-flex items-center gap-2" @click="advanceTo(currentIndex + 1)">
                {{ forwardLabel }}
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton
                v-else-if="activeId === 'organization'"
                theme="neutral"
                size="sm"
                class="relative inline-flex items-center gap-2 animate-shadow-pulse"
                style="--shadow-pulse-iterations: 3; --shadow-pulse-color: var(--color-border-strong)"
                :aria-label="ctaLabel"
                :aria-busy="ctaLoading"
                :disabled="ctaLoading"
                @click="handleCtaClick">
                <span class="inline-flex items-center gap-2" :class="ctaLoading && 'invisible'">
                    {{ ctaLabel }}
                    <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </span>
                <LoadingSpinner v-if="ctaLoading" class="absolute inset-0 m-auto h-4 shrink-0" />
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import CoachingContextGraph from "~vue/ChatHome/CoachingContextGraph.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import OrganizationIcon from "~vue/icons/OrganizationIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const AUTO_ADVANCE_DELAY = 3000; // ms between auto-advances

const {
    connectionsEnabled = true,
    collabProfileEnabled = true,
    calendarEnabled = true,
    ctaLoading = false,
    location,
} = defineProps<{
    firstName?: string;
    location: "home" | "private_profile";
    // Feature flags mirrored from the coaching journey accordion. Default true preserves the full carousel.
    connectionsEnabled?: boolean;
    collabProfileEnabled?: boolean;
    calendarEnabled?: boolean;
    ctaLoading?: boolean;
}>();

const emit = defineEmits<{
    "cta-click": [];
    dismiss: [];
}>();

const { t } = useI18n();

function logComponentInteraction(event: AmplitudeEventNames, extra: Record<string, unknown> = {}) {
    logUserInteraction(event, { location, ...extra });
}

function handleCtaClick() {
    logComponentInteraction(AmplitudeEventNames.COACHING_CONTEXT_CAROUSEL_CTA_CLICKED);
    emit("cta-click");
}

const ctaLabel = computed(() => t("chat.home.coachingContext.cta"));

// Panel 3 copy varies with the collab-profile feature; panel 4 with calendar.
const relationshipsCopy = computed(() => (collabProfileEnabled ? t("chat.home.coachingContext.relationshipsCopyWithCollab") : t("chat.home.coachingContext.relationshipsCopy")));
const weekCopy = computed(() => (calendarEnabled ? t("chat.home.coachingContext.weekCopyWithCalendar") : t("chat.home.coachingContext.weekCopy")));

// Ordered context dimensions after the welcome panel — single source of truth for
// icons and colors. Headings and copy are resolved reactively in `contextPanels` below.
// Each panel renders either a Bootstrap icon class (`icon`) or a custom Vue icon component (`iconComponent`).
type ContextPanel = { id: string; icon?: string; iconComponent?: Component; color: string; bg: string };

const CONTEXT_PANELS: ContextPanel[] = [
    { id: "you", icon: "bi-person-fill", color: "#CD2F7B", bg: "#fce4ef" },
    { id: "relationships", icon: "bi-people-fill", color: "#FB945E", bg: "#fef3e0" },
    { id: "week", icon: "bi-calendar4", color: "#14C8EB", bg: "#e0f7fd" },
    { id: "organization", iconComponent: OrganizationIcon, color: "#5852C4", bg: "#eef0fb" },
];

// Dot colors derive from the panel definitions (keyed by panel id).
const DIMENSION_COLORS: Record<string, string> = Object.fromEntries(CONTEXT_PANELS.map((p) => [p.id, p.color]));

// Per-panel data with copy resolved (relationships/week vary by feature flag).
const contextPanels = computed(() => {
    const textById: Record<string, { heading: string; copy: string }> = {
        you: { heading: t("chat.home.coachingContext.youHeading"), copy: t("chat.home.coachingContext.youCopy") },
        relationships: { heading: t("chat.home.coachingContext.relationshipsHeading"), copy: relationshipsCopy.value },
        week: { heading: t("chat.home.coachingContext.weekHeading"), copy: weekCopy.value },
        organization: { heading: t("chat.home.coachingContext.organizationHeading"), copy: t("chat.home.coachingContext.organizationCopy") },
    };
    return CONTEXT_PANELS.map((panel) => ({ ...panel, ...textById[panel.id] }));
});

const itemIds = computed<string[]>(() => (connectionsEnabled ? ["welcome", "you", "relationships", "week", "organization"] : ["welcome", "you", "week", "organization"]));
const activeId = ref<string>("");
const currentIndex = computed(() => itemIds.value.indexOf(activeId.value));

// Dimension panels (everything after the welcome panel) and each one's dot index.
const dimensionPanels = computed(() => itemIds.value.filter((id) => id !== "welcome"));
const dimensionDots = computed(() => dimensionPanels.value.map((id) => ({ dotColor: DIMENSION_COLORS[id] })));
const dimensionIndex = (id: string) => dimensionPanels.value.indexOf(id);
const activeDimensionIndex = computed(() => dimensionIndex(activeId.value));

const forwardLabel = computed(() => {
    switch (activeId.value) {
        case "welcome":
            return t("chat.home.coachingContext.forwardWelcome");
        case "you":
            // Skips straight from "You" to "Your week" when the relationships panel is hidden.
            return connectionsEnabled ? t("chat.home.coachingContext.forwardYou") : t("chat.home.coachingContext.forwardRelationships");
        case "relationships":
            return t("chat.home.coachingContext.forwardRelationships");
        case "week":
            return t("chat.home.coachingContext.forwardWeek");
        default:
            return "";
    }
});

// Highest context ring revealed for the current panel (-1 hides all rings).
const PANEL_GRAPH_STEP: Record<string, number> = { you: 0, relationships: 1, week: 2, organization: 3 };
const PANEL_GRAPH_STEP_NO_RELATIONSHIPS: Record<string, number> = { you: 0, week: 1, organization: 2 };

const graphStep = computed<number>(() => {
    const map = connectionsEnabled ? PANEL_GRAPH_STEP : PANEL_GRAPH_STEP_NO_RELATIONSHIPS;
    return map[activeId.value] ?? -1;
});
const hasGraph = computed(() => graphStep.value >= 0);

// Auto-advance logic
let autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;
const userInteracted = ref(false);

function clearAutoAdvance() {
    if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
        autoAdvanceTimer = null;
    }
}

function scheduleAutoAdvance() {
    clearAutoAdvance();
    if (userInteracted.value) {
        return;
    }
    if (currentIndex.value >= itemIds.value.length - 1) {
        return;
    }
    autoAdvanceTimer = setTimeout(() => {
        if (currentIndex.value < itemIds.value.length - 1) {
            activeId.value = itemIds.value[currentIndex.value + 1];
            scheduleAutoAdvance();
        }
    }, AUTO_ADVANCE_DELAY);
}

function advanceTo(index: number) {
    userInteracted.value = true;
    clearAutoAdvance();
    activeId.value = itemIds.value[index];
}

watch(activeId, () => {
    if (!userInteracted.value) {
        scheduleAutoAdvance();
    }
});

onMounted(() => {
    logComponentInteraction(AmplitudeEventNames.COACHING_CONTEXT_CAROUSEL_SHOWN);
    scheduleAutoAdvance();
});

onUnmounted(() => {
    clearAutoAdvance();
});
</script>
